import { useGetSettings, getGetSettingsQueryKey } from "@workspace/api-client-react";
import { DEFAULT_TARGET_MARGIN, marginTone, type MarginTone } from "@workspace/totals";

/**
 * The target margin to judge a margin against, and the colour to show it in:
 * green at or above the target, amber below it, red below half of it or when
 * the job loses money (marginTone() in @workspace/totals).
 *
 * The default comes from Settings → Margin target. A quote can set its own
 * (estimates.target_margin): pass it as `quoteTarget`, or per call to tone()
 * when colouring a list of quotes.
 */
export function useTargetMargin(quoteTarget?: number | null) {
  const { data: settings } = useGetSettings({
    query: { queryKey: getGetSettingsQueryKey() },
  });
  const defaultTarget = settings?.targetMargin ?? DEFAULT_TARGET_MARGIN;
  const target = quoteTarget ?? defaultTarget;
  const tone = (margin: number, ownTarget?: number | null): MarginTone =>
    marginTone(margin, ownTarget ?? target);
  return { target, defaultTarget, tone };
}

/** Text colours for a margin tone on a light background. */
export const TONE_TEXT: Record<MarginTone, string> = {
  good: "text-green-600",
  thin: "text-amber-600",
  low: "text-red-500",
};

/** Hex colours for inline styles (project stat boxes). */
export const TONE_HEX: Record<MarginTone, string> = {
  good: "#10b981",
  thin: "#f59e0b",
  low: "#ef4444",
};
